/**
 * Map: a MapLibre canvas with a pin and popup per place in the markup.
 */
( function() {
	const containers = document.querySelectorAll( '[data-wgi-map]' );

	if ( ! containers.length || 'undefined' === typeof maplibregl ) {
		return;
	}

	if ( ! hasWebGL() ) {
		return;
	}

	const reduceMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

	const POPUP_OFFSET = 34;
	const EDGE = 12;

	const CLEARANCE = 24;
	const MAX_ZOOM = 15;

	/**
	 * Whether this browser can give MapLibre a context to draw into.
	 *
	 * @return {boolean} True when WebGL is available.
	 */
	function hasWebGL() {
		try {
			const canvas = document.createElement( 'canvas' );

			return !! ( canvas.getContext( 'webgl2' ) || canvas.getContext( 'webgl' ) );
		} catch ( error ) {
			return false;
		}
	}

	/**
	 * The box round a set of places.
	 *
	 * @param {Array} places Places with a `lngLat`.
	 * @return {maplibregl.LngLatBounds} The bounds.
	 */
	function boundsOf( places ) {
		const bounds = new maplibregl.LngLatBounds();

		places.forEach( function( place ) {
			bounds.extend( place.lngLat );
		} );

		return bounds;
	}

	containers.forEach( function( container ) {
		const cards = Array.from( container.querySelectorAll( '[data-wgi-map-place]' ) );
		const places = [];

		const clear = container.dataset.clear ? document.querySelector( container.dataset.clear ) : null;

		cards.forEach( function( card ) {
			const lngLat = [
				parseFloat( card.dataset.lng ),
				parseFloat( card.dataset.lat ),
			];

			if ( ! isFinite( lngLat[ 0 ] ) || ! isFinite( lngLat[ 1 ] ) ) {
				return;
			}

			places.push( {
				id: card.dataset.id || String( places.length ),
				label: card.dataset.label || '',
				card,
				lngLat,
				visible: ! card.hidden,
			} );
		} );

		const centre = [
			parseFloat( container.dataset.lng ),
			parseFloat( container.dataset.lat ),
		];

		const hasCentre = isFinite( centre[ 0 ] ) && isFinite( centre[ 1 ] );
		const zoom = parseFloat( container.dataset.zoom ) || 15;

		if ( ! hasCentre && ! places.length ) {
			return;
		}

		cards.forEach( function( card ) {
			card.remove();
			card.hidden = false;
		} );

		container.classList.add( 'is-live' );

		const options = {
			container,
			style: container.dataset.style,
			scrollZoom: false,
			pitchWithRotate: false,
			dragRotate: false,
			attributionControl: { compact: true },
		};

		const shown = places.filter( function( place ) {
			return place.visible;
		} );

		let bounds = null;

		if ( hasCentre ) {
			options.center = centre;
			options.zoom = zoom;
		} else if ( 1 === shown.length ) {
			options.center = shown[ 0 ].lngLat;
			options.zoom = zoom;
		} else {
			bounds = boundsOf( shown.length ? shown : places );
		}

		const map = new maplibregl.Map( options );

		if ( bounds ) {
			map.fitBounds( bounds, { padding: fitPadding( bounds ), maxZoom: MAX_ZOOM, duration: 0 } );
		}

		map.touchZoomRotate.disableRotation();
		map.addControl( new maplibregl.NavigationControl( { showCompass: false } ), 'top-right' );

		const engage = function() {
			map.scrollZoom.enable();
			container.classList.add( 'is-engaged' );
		};

		const disengage = function() {
			map.scrollZoom.disable();
			container.classList.remove( 'is-engaged' );
		};

		container.addEventListener( 'click', engage );
		container.addEventListener( 'focusin', engage );
		container.addEventListener( 'mouseleave', disengage );
		container.addEventListener( 'focusout', function( event ) {
			if ( ! container.contains( event.relatedTarget ) ) {
				disengage();
			}
		} );

		const single = container.hasAttribute( 'data-open' );

		let current = null;

		/**
		 * The overlay's box in frame pixels, or null when it is not over the map.
		 *
		 * @return {Object|null} `top`, `right`, `bottom` and `left`, from the frame's top-left.
		 */
		function overlay() {
			if ( ! clear ) {
				return null;
			}

			const frame = container.getBoundingClientRect();
			const box = clear.getBoundingClientRect();

			if ( box.right <= frame.left || box.left >= frame.right || box.bottom <= frame.top || box.top >= frame.bottom ) {
				return null;
			}

			return {
				top: box.top - frame.top,
				right: box.right - frame.left,
				bottom: box.bottom - frame.top,
				left: box.left - frame.left,
			};
		}

		/**
		 * Padding for a fitted view that keeps pins clear of the edges and the overlay.
		 *
		 * @param {maplibregl.LngLatBounds} bounds The box round the pins.
		 * @return {Object} MapLibre padding.
		 */
		function fitPadding( bounds ) {
			const base = { top: 72, bottom: 48, left: 48, right: 48 };
			const over = overlay();

			if ( ! over ) {
				return base;
			}

			const width = container.clientWidth;
			const height = container.clientHeight;

			const candidates = [
				{ top: over.bottom + CLEARANCE },
				{ bottom: height - over.top + CLEARANCE },
				{ left: over.right + CLEARANCE },
				{ right: width - over.left + CLEARANCE },
			].map( function( side ) {
				const padding = Object.assign( {}, base );

				Object.keys( side ).forEach( function( edge ) {
					padding[ edge ] = Math.max( padding[ edge ], side[ edge ] );
				} );

				return padding;
			} );

			let best = null;

			candidates.forEach( function( padding ) {
				const camera = map.cameraForBounds( bounds, { padding, maxZoom: MAX_ZOOM } );

				if ( camera && ( ! best || camera.zoom > best.zoom ) ) {
					best = { zoom: camera.zoom, padding };
				}
			} );

			return best ? best.padding : base;
		}

		/**
		 * Seat the pin low enough that the popup always opens above it.
		 *
		 * @param {maplibregl.Popup} popup The open popup.
		 */
		function seatPin( popup ) {
			const element = popup.getElement();

			if ( ! element ) {
				return;
			}

			const height = container.clientHeight;
			const needed = element.offsetHeight + POPUP_OFFSET + EDGE;
			const top = Math.max( 0, Math.min( 2 * needed - height, height * 0.5 ) );

			map.setPadding( { top, bottom: 0, left: 0, right: 0 } );
		}

		/**
		 * Pan the map by just enough that an open popup is wholly in frame.
		 *
		 * @param {maplibregl.Popup} popup The open popup.
		 */
		function keepInView( popup ) {
			window.requestAnimationFrame( function() {
				const element = popup.getElement();

				if ( ! element || ! popup.isOpen() ) {
					return;
				}

				const frame = container.getBoundingClientRect();
				const box = element.getBoundingClientRect();

				let dx = 0;
				let dy = 0;

				if ( box.left < frame.left + EDGE ) {
					dx = box.left - ( frame.left + EDGE );
				} else if ( box.right > frame.right - EDGE ) {
					dx = box.right - ( frame.right - EDGE );
				}

				if ( box.top < frame.top + EDGE ) {
					dy = box.top - ( frame.top + EDGE );
				} else if ( box.bottom > frame.bottom - EDGE ) {
					dy = box.bottom - ( frame.bottom - EDGE );
				}

				const over = overlay();

				if (
					over &&
					box.right > frame.left + over.left &&
					box.left < frame.left + over.right &&
					box.bottom > frame.top + over.top &&
					box.top < frame.top + over.bottom
				) {
					dy = box.top - ( frame.top + over.bottom + EDGE );
				}

				if ( dx || dy ) {
					map.panBy( [ dx, dy ], { duration: reduceMotion ? 0 : 300 } );
				}
			} );
		}

		places.forEach( function( place ) {
			const popup = new maplibregl.Popup( {
				offset: POPUP_OFFSET,
				closeButton: true,
				closeOnClick: ! single,
				focusAfterOpen: false,
				maxWidth: 'min(30rem, 88vw)',
				className: 'wgi-map-popup',
			} ).setDOMContent( place.card );

			const pin = document.createElement( 'button' );
			pin.type = 'button';
			pin.className = 'wgi-map-pin';
			pin.setAttribute( 'aria-label', ( container.dataset.markerLabel || '%s' ).replace( '%s', place.label ) );

			const marker = new maplibregl.Marker( { element: pin, anchor: 'bottom' } )
				.setLngLat( place.lngLat )
				.setPopup( popup );

			if ( place.visible ) {
				marker.addTo( map );
			}

			popup.on( 'open', function() {
				if ( current && current !== place ) {
					current.popup.remove();
				}

				current = place;

				const close = popup.getElement().querySelector( '.maplibregl-popup-close-button' );

				if ( close ) {
					close.setAttribute( 'aria-label', container.dataset.closeLabel || 'Close' );
				}

				if ( ! single ) {
					keepInView( popup );
				}
			} );

			popup.on( 'close', function() {
				if ( current === place ) {
					current = null;
				}
			} );

			if ( ! reduceMotion ) {
				pin.classList.add( 'is-animated' );
			}

			place.popup = popup;
			place.marker = marker;
			place.pin = pin;
		} );

		if ( 'undefined' !== typeof ResizeObserver ) {
			new ResizeObserver( function() {
				map.resize();

				if ( single && current ) {
					seatPin( current.popup );
				}
			} ).observe( container );
		}

		container.addEventListener( 'keydown', function( event ) {
			if ( 'Escape' === event.key && current ) {
				const pin = current.pin;

				current.popup.remove();
				pin.focus();
			}
		} );

		let styled = false;
		let restored = false;

		map.once( 'styledata', function() {
			styled = true;
		} );

		map.on( 'error', function() {
			if ( styled || restored ) {
				return;
			}

			restored = true;
			map.remove();
			container.classList.remove( 'is-live', 'is-engaged' );

			places.forEach( function( place ) {
				place.card.hidden = ! place.visible;
				container.appendChild( place.card );
			} );

			delete container.wgiMap;
			container.dispatchEvent( new CustomEvent( 'wgi-map:removed', { bubbles: true } ) );
		} );

		/**
		 * The place a script means by an id.
		 *
		 * @param {string} id The place's id.
		 * @return {Object|undefined} The place.
		 */
		function find( id ) {
			return places.find( function( place ) {
				return place.id === String( id );
			} );
		}

		const api = {
			map,
			places,

			/**
			 * Open a place's popup, bringing it into view.
			 *
			 * @param {string} id The place's id.
			 */
			open( id ) {
				const place = find( id );

				if ( ! place || ! place.visible || restored ) {
					return;
				}

				if ( place.popup.isOpen() ) {
					keepInView( place.popup );
					return;
				}

				place.marker.togglePopup();
			},

			/**
			 * Close whichever popup is open.
			 */
			close() {
				if ( current ) {
					current.popup.remove();
				}
			},

			/**
			 * Put a place on the map, or take it off.
			 *
			 * @param {string}  id      The place's id.
			 * @param {boolean} visible Whether it should be shown.
			 */
			show( id, visible ) {
				const place = find( id );

				if ( ! place || restored ) {
					return;
				}

				visible = !! visible;

				if ( visible === place.visible ) {
					return;
				}

				place.visible = visible;

				if ( visible ) {
					place.marker.addTo( map );
				} else {
					place.marker.remove();
				}
			},

			/**
			 * Fit the view round whatever is showing.
			 */
			fit() {
				if ( restored ) {
					return;
				}

				const visible = places.filter( function( place ) {
					return place.visible;
				} );

				if ( ! visible.length ) {
					return;
				}

				const duration = reduceMotion ? 0 : 600;

				if ( 1 === visible.length ) {
					map.easeTo( { center: visible[ 0 ].lngLat, zoom, duration } );
					return;
				}

				const bounds = boundsOf( visible );

				map.fitBounds( bounds, { padding: fitPadding( bounds ), maxZoom: MAX_ZOOM, duration } );
			},
		};

		container.wgiMap = api;
		container.dispatchEvent( new CustomEvent( 'wgi-map:ready', { bubbles: true, detail: api } ) );

		if ( ! single || ! places.length ) {
			return;
		}

		const first = places[ 0 ];

		first.marker.togglePopup();
		seatPin( first.popup );

		if ( document.fonts && document.fonts.ready ) {
			document.fonts.ready.then( function() {
				if ( current ) {
					seatPin( current.popup );
				}
			} );
		}
	} );
}() );
